import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, Button, Card, Spinner } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { authFetch } from "../utils/authFetch";

export default function D406Edit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [main, setMain] = useState({
    bxluna: "",
    bxan: "",
    cui: "",
    firma: "",
    adresa: "",
    telefon: "",
    fax: "",
    email: "",
    banca: "",
    cont: "",
    reprezentant: "",
    nume: "",
    prenume: "",
    functia: "",
    suma_control: 0
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const mainData = await authFetch(`/d406/${id}`);
      if (mainData && mainData.length > 0) {
        setMain(mainData[0]);
      } else {
        toast.error("Declarația D406 SAF-T nu a fost găsită.");
        navigate("/d406");
      }
    } catch (e) {
      toast.error(`Eroare la încărcarea datelor: ${e?.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const handleMainChange = (e) => {
    const { name, value } = e.target;
    setMain((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await authFetch("/d406/", {
        method: "POST",
        body: JSON.stringify({ ...main, bxid: id })
      });
      if (res.result === "pass") {
        toast.success("Datele antet D406 au fost salvate!");
        loadData();
      } else {
        toast.error(res.message || "Eroare la salvare.");
      }
    } catch (err) {
      toast.error(`Eroare la salvare: ${err?.message || err}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadXml = async (isActive = false) => {
    const url = isActive ? `/d406/${id}/xml-active` : `/d406/${id}/xml`;
    const label = isActive ? "SAF-T Active (Mijloace Fixe)" : "SAF-T Lunar";
    const filenamePrefix = isActive ? "D406Active" : "D406";
    
    try {
      toast.info(`Se generează fișierul ${label}...`);
      const blob = await authFetch(url, {
        responseType: "blob",
      });
      const file = new Blob([blob], { type: "application/xml" });
      const fileURL = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = fileURL;
      link.setAttribute("download", `${filenamePrefix}_${main.bxan}_${String(main.bxluna).padStart(2, "0")}.xml`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(`Descărcare ${label} inițiată!`);
    } catch (err) {
      toast.error(`Eroare la descărcarea XML: ${err?.message || err}`);
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Se încarcă datele declarației D406...</p>
      </div>
    );
  }

  return (
    <Container className="mt-4 pb-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          Editare Declarație D406 SAF-T (Luna {main.bxluna} / Anul {main.bxan})
        </h2>
        <div>
          <Button variant="outline-secondary" onClick={() => navigate("/d406")} className="me-2">
            Înapoi la listă
          </Button>
          <Button variant="success" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm" /> : "Salvează antet"}
          </Button>
        </div>
      </div>

      <Row>
        <Col md={8}>
          <Form onSubmit={handleSave}>
            <Card className="mb-4 shadow-sm border-0">
              <Card.Header className="bg-dark text-white fw-bold">Date Identificare Companie</Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Nume Firmă</Form.Label>
                      <Form.Control name="firma" value={main.firma || ""} onChange={handleMainChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>CUI / CIF</Form.Label>
                      <Form.Control name="cui" value={main.cui || ""} onChange={handleMainChange} />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>Adresă completă</Form.Label>
                  <Form.Control as="textarea" rows={2} name="adresa" value={main.adresa || ""} onChange={handleMainChange} />
                </Form.Group>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Telefon</Form.Label>
                      <Form.Control name="telefon" value={main.telefon || ""} onChange={handleMainChange} />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Fax</Form.Label>
                      <Form.Control name="fax" value={main.fax || ""} onChange={handleMainChange} />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email</Form.Label>
                      <Form.Control name="email" value={main.email || ""} onChange={handleMainChange} />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Bancă</Form.Label>
                      <Form.Control name="banca" value={main.banca || ""} onChange={handleMainChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Cont IBAN</Form.Label>
                      <Form.Control name="cont" value={main.cont || ""} onChange={handleMainChange} />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-4 shadow-sm border-0">
              <Card.Header className="bg-dark text-white fw-bold">Reprezentant Legal / Declarant</Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Calitate / Reprezentant</Form.Label>
                      <Form.Control name="reprezentant" value={main.reprezentant || ""} onChange={handleMainChange} placeholder="Ex: Administrator" />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Funcție</Form.Label>
                      <Form.Control name="functia" value={main.functia || ""} onChange={handleMainChange} />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Nume</Form.Label>
                      <Form.Control name="nume" value={main.nume || ""} onChange={handleMainChange} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Prenume</Form.Label>
                      <Form.Control name="prenume" value={main.prenume || ""} onChange={handleMainChange} />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Form>
        </Col>

        <Col md={4}>
          <Card className="shadow-sm border-0 mb-4 bg-light">
            <Card.Header className="bg-dark text-white fw-bold">Generare XML SAF-T</Card.Header>
            <Card.Body className="d-flex flex-column gap-3 p-4">
              <p className="text-muted small mb-0">
                Generați fișierele XML SAF-T corespunzătoare schemei oficiale ANAF. După generare, fișierul va fi descărcat automat.
              </p>
              
              <Button variant="success" size="lg" className="w-100 py-3 fw-bold" onClick={() => handleDownloadXml(false)}>
                Generare XML Lunar
              </Button>
              
              <Button variant="warning" size="lg" className="w-100 py-3 fw-bold text-white" onClick={() => handleDownloadXml(true)}>
                Generare XML Active
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
